import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';

@Index('IDX_isDefault', ['isDefault'], {})
@Index('IDX_payment_processor_config_fk_company__id', ['fkCompanyId'], {})
/**
 * PD-16204 — one configuration per processor type PER SCOPE, where the scope
 * is a company or the whole tenant (`fk_company__id` NULL).
 *
 * MySQL treats NULLs as distinct inside a unique index, so this index cannot
 * stop two tenant-wide rows of the same processor type on its own;
 * PaymentConfigService checks the scope before every insert. The index still
 * guards the company-scoped rows, which is where the second row would
 * otherwise be silently created by a double submit. It replaces the former
 * `UK_processorType` (processorType alone), which is dropped by the
 * AddCompanyScopeToPaymentConfiguration migration.
 */
@Index('UK_processorType_company', ['processorType', 'fkCompanyId'], {
	unique: true,
})
@Entity('payment_processor_config')
export class PaymentProcessorConfigEntity extends BaseEntity {
	@Column('enum', {
		name: 'processorType',
		// Uniqueness is declared ONCE, by the @Index above. Repeating it as
		// `unique: true` here made TypeORM emit a SECOND unique index with a
		// generated name (IDX_98cd328412ab45f329f2927dce) alongside
		// UK_processorType — same column, same constraint, twice (PD-16021).
		// 'mock' (PD-15708 Q9) is the in-process test gateway; PaymentConfigService
		// refuses to store it on a production environment. Adding a value to a
		// MySQL enum needs a migration (sync never ALTERs): see
		// AddMockPaymentProcessorType.
		enum: ['authorize_net', 'cybersource', 'nmi', 'adyen', 'mock'],
	})
	public processorType!:
		| 'authorize_net'
		| 'cybersource'
		| 'nmi'
		| 'adyen'
		| 'mock';

	/**
	 * PD-16204 — the company this configuration belongs to, or NULL for the
	 * tenant-wide configuration every company without one of its own uses.
	 *
	 * Resolution is "most specific wins": a payment for a company is routed
	 * through that company's default configuration when the company has any
	 * configuration at all, and through the tenant-wide default otherwise.
	 * `PaymentConfigService.resolveConfigForCompany` owns that rule; nothing
	 * else should read `isDefault` across scopes.
	 *
	 * Named `fk_company__id` rather than the legacy `_fk__object__id_reference`
	 * + `_referenceId` pair that `_api_configuration` still carries: every
	 * tenant now has a standard `company` object, so the generic indirection
	 * is no longer needed. The legacy pair is still written when this
	 * configuration is pushed to the payment lambda, because the SM1 server
	 * resolves by it.
	 */
	@Column('int', { name: 'fk_company__id', nullable: true, unsigned: true })
	public fkCompanyId!: number | null;

	/**
	 * The default configuration WITHIN ITS SCOPE — the tenant-wide default
	 * when `fkCompanyId` is NULL, that company's default otherwise. A scope
	 * with several processors configured has exactly one default; a scope
	 * with none has no row at all.
	 */
	@Column('tinyint', { name: 'isDefault', default: () => "'0'" })
	public isDefault!: number;

	@Column('json', { name: 'credentials' })
	public credentials!: object;

	@Column('enum', {
		name: 'environment',
		enum: ['sandbox', 'production'],
		default: () => "'sandbox'",
	})
	public environment!: 'sandbox' | 'production';

	@ManyToOne(
		() => CompanyEntity,
		(company) => company.paymentProcessorConfigs,
		{
			onDelete: 'NO ACTION',
			onUpdate: 'NO ACTION',
		},
	)
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity | null;

	@OneToMany(
		() => MPaymentPaymentMethodEntity,
		(paymentMethod) => paymentMethod.fkPaymentProcessorConfig,
		{},
	)
	public paymentMethods!: Array<MPaymentPaymentMethodEntity>;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(transaction) => transaction.fkPaymentProcessorConfig,
		{},
	)
	public transactions!: Array<MPaymentTransactionEntity>;
}
